// services/secureAuthService.ts
// Serviço de autenticação seguro usando cookies httpOnly

import api from './api';
import { API_ENDPOINTS } from '../config/api.config';
import { clearAuthCache, setAuthCache } from '../utils/authCache';

// Interface User moderna - APENAS dados essenciais
// Papéis e permissões são gerenciados pelo RBACContext
export interface User {
  id: string;
  name: string;
  email: string;
  projectId?: string;
  // ❌ REMOVIDO: role, papelPlataforma (agora via RBAC)
}

// Interface para dados de registro
export interface RegisterData {
  fullName: string;
  email: string;
  password: string;
  projectName: string;
  phone: string;
  website?: string;
  plan: string;
}

// Interface para resposta de login
interface LoginResponse {
  success: boolean;
  message?: string;
  user?: User;
  csrfToken?: string;
}

// Interface para resposta de registro
interface RegisterResponse {
  success: boolean;
  message: string;
  user?: User;
  csrfToken?: string;
  data?: {
    userId: string;
    email: string;
    emailSent: boolean;
    nextStep: string;
    message: string;
  };
}

/**
 * Serviço de autenticação seguro usando cookies httpOnly
 */
export const authService = {
  /**
   * Faz login do usuário
   */
  login: async (email: string, password: string): Promise<LoginResponse> => {
    try {
      console.log('🔐 Iniciando login seguro...');

      const response = await api.post<LoginResponse>(
        API_ENDPOINTS.auth.login,
        { email, password }
      );

      const data = response.data;

      if (data.success && data.user) {
        console.log('✅ Login realizado com sucesso');
        
        // Normalizar estrutura do usuário para interface limpa
        const normalizedUser = {
          id: data.user.id,
          name: data.user.name,
          email: data.user.email,
          projectId: data.user.projectId
        };
        
        // Salvar apenas dados não sensíveis no sessionStorage
        sessionStorage.setItem('user', JSON.stringify(normalizedUser));
        sessionStorage.removeItem('selectedProjectId'); // Limpar projeto anterior
        
        // Atualizar cache de autenticação
        setAuthCache(true, normalizedUser);
        
        // Log para debug (sem dados sensíveis)
        console.log('👤 Usuário logado:', {
          id: normalizedUser.id,
          name: normalizedUser.name,
          email: normalizedUser.email,
          projectId: normalizedUser.projectId
        });

        return { ...data, user: normalizedUser };
      } else {
        throw new Error(data.message || 'Erro no login');
      }
    } catch (error: any) {
      console.error('❌ Erro durante login:', error);
      
      // ✅ PRESERVAR DADOS ESTRUTURADOS para casos especiais (ex: email não verificado)
      if (error.response?.data?.errorCode) {
        const errorWithData = new Error(error.response.data.message || 'Erro no login');
        (errorWithData as any).errorCode = error.response.data.errorCode;
        (errorWithData as any).data = error.response.data.data;
        (errorWithData as any).response = error.response;
        throw errorWithData;
      }
      
      // Extrair mensagem de erro para outros casos
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          'Erro ao fazer login';

      throw new Error(errorMessage);
    }
  },

  /**
   * Registra um novo usuário
   */
  register: async (data: RegisterData): Promise<RegisterResponse> => {
    try {
      console.log('📝 Iniciando registro seguro...');

      const response = await api.post<RegisterResponse>(
        API_ENDPOINTS.auth.register,
        data
      );

      const responseData = response.data;

      if (responseData.success) {
        console.log('✅ Registro realizado com sucesso');

        // ✅ NOVO: Verificar se é fluxo de verificação de email
        if (responseData.data?.nextStep === 'email_verification') {
          console.log('📧 Registro com verificação de email - aguardando confirmação');
          
          // NÃO fazer login automático
          // NÃO salvar dados do usuário em sessão
          // Apenas retornar dados para mostrar página de confirmação
          
          return responseData;
        }

        // LEGACY: Manter comportamento antigo se não tiver verificação de email
        // Backend já definiu cookie httpOnly de autenticação
        // Salvar dados do usuário localmente
        if (responseData.user) {
          // Normalizar estrutura do usuário para interface limpa
          const normalizedUser = {
            id: responseData.user.id,
            name: responseData.user.name,
            email: responseData.user.email,
            projectId: responseData.user.projectId
          };
          
          sessionStorage.setItem('user', JSON.stringify(normalizedUser));
          sessionStorage.removeItem('selectedProjectId');
          
          // Atualizar cache de autenticação
          setAuthCache(true, normalizedUser);
          
          console.log('👤 Usuário registrado e logado automaticamente via cookie');
        }

        return responseData;
      } else {
        throw new Error(responseData.message || 'Erro no registro');
      }
    } catch (error: any) {
      console.error('❌ Erro durante registro:', error);
      
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          'Erro ao fazer registro';

      return {
        success: false,
        message: errorMessage
      };
    }
  },

  /**
   * Faz logout do usuário
   */
  logout: async (): Promise<void> => {
    try {
      console.log('🔓 Iniciando logout seguro...');

      // Marcar que está em processo de logout
      sessionStorage.setItem('logging_out', 'true');

      // Limpar dados locais primeiro
      // CRÍTICO: Limpeza completa de dados de sessão
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('selectedProjectId');
      sessionStorage.removeItem('selectedAgentId');
      sessionStorage.removeItem('fromAgentsList');
      sessionStorage.removeItem('prev_auth_state');

      // Limpar caches de projeto (evitar contaminação de sessão)
      try {
        // Limpar cache de projetos se existir
        if (window.localStorage.getItem('projectCache')) {
          window.localStorage.removeItem('projectCache');
        }
        if (window.sessionStorage.getItem('projectListCache')) {
          window.sessionStorage.removeItem('projectListCache');
        }
        
        // Limpar dados do agente interno de todos os usuários (por segurança)
        const keys = Object.keys(localStorage);
        keys.forEach(key => {
          if (key.startsWith('internal-agent-messages-') || key.startsWith('internal-agent-settings-')) {
            localStorage.removeItem(key);
          }
        });

        // Limpar também memórias no AI Engine (se houver usuário logado)
        const currentUser = authService.getCurrentUser();
        if (currentUser && currentUser.id && currentUser.id !== 'anonymous') {
          try {
            const { aiEngineService } = require('../services/aiEngineService');
            await aiEngineService.clearUserMemory(currentUser.id);
            console.log('✅ Memória do usuário limpa no AI Engine durante logout');
          } catch (error) {
            console.warn('⚠️ Erro ao limpar memória no AI Engine:', error);
          }
        }
      } catch (cacheError) {
        console.warn('⚠️ Erro ao limpar cache de projetos:', cacheError);
      }

      try {
        // Tentar fazer logout no servidor (limpar cookies)
        await api.post(API_ENDPOINTS.auth.logout);
        console.log('✅ Logout no servidor realizado');
      } catch (serverError) {
        console.warn('⚠️ Erro ao fazer logout no servidor:', serverError);
        // Continuar mesmo se o logout no servidor falhar
      }

      // Limpar CSRF token
      api.clearCsrfToken();
      
      // Limpar cache de autenticação
      clearAuthCache();

      console.log('✅ Logout local realizado com sucesso');
    } catch (error) {
      console.error('❌ Erro durante logout:', error);
      
      // Garantir limpeza local mesmo com erro
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('selectedProjectId');
      api.clearCsrfToken();
      clearAuthCache();
    } finally {
      // Remover flag de logout
      sessionStorage.removeItem('logging_out');
    }
  },

  /**
   * Verifica se o usuário está autenticado
   */
  isAuthenticated: (): boolean => {
    // Verificar se está em processo de logout
    if (sessionStorage.getItem('logging_out') === 'true') {
      return false;
    }

    // Verificar se estamos em páginas públicas
    const currentPath = window.location.pathname;
    const publicPaths = ['/login', '/register', '/register-full', '/', '/forgot-password'];
    
    if (publicPaths.includes(currentPath)) {
      return false;
    }

    // Verificar se temos dados do usuário
    const userStr = sessionStorage.getItem('user');
    return !!userStr;
  },

  /**
   * Obtém o usuário atual
   */
  getCurrentUser: (): User | null => {
    try {
      const userStr = sessionStorage.getItem('user');
      if (!userStr) return null;

      return JSON.parse(userStr);
    } catch (error) {
      console.error('❌ Erro ao recuperar usuário:', error);
      return null;
    }
  },

  /**
   * Verifica se a autenticação é válida no servidor
   */
  checkAuth: async (): Promise<boolean> => {
    try {
      const response = await api.get(API_ENDPOINTS.auth.verify);
      return response.data.success === true;
    } catch (error) {
      console.error('❌ Erro ao verificar autenticação:', error);
      return false;
    }
  },

  /**
   * Verifica se um email já está em uso
   */
  checkEmailExists: async (email: string): Promise<boolean> => {
    try {
      console.log('📧 Verificando disponibilidade do email...');
      
      const response = await api.post(API_ENDPOINTS.auth.checkEmail, { email });
      const data = response.data as { exists: boolean; success: boolean };
      
      return data.exists;
    } catch (error: any) {
      console.error('❌ Erro ao verificar email:', error);
      
      // Em caso de erro de rede/servidor, assumir que email não existe
      // para não bloquear o registro
      if (error.response?.status === 500 || !error.response) {
        console.warn('⚠️ Erro de servidor, assumindo email disponível');
        return false;
      }
      
      return false;
    }
  },

  /**
   * Força renovação do token de autenticação
   */
  refreshToken: async (): Promise<void> => {
    try {
      console.log('🔄 Renovando token de autenticação...');
      
      const response = await api.post(API_ENDPOINTS.auth.refreshToken);
      
      if (response.data.success) {
        console.log('✅ Token renovado com sucesso');
        
        // Atualizar dados do usuário se retornados
        if (response.data.user) {
          sessionStorage.setItem('user', JSON.stringify(response.data.user));
        }
      } else {
        throw new Error('Falha ao renovar token');
      }
    } catch (error) {
      console.error('❌ Erro ao renovar token:', error);
      
      // Limpar dados em caso de falha
      sessionStorage.removeItem('user');
      throw error;
    }
  },

  /**
   * Obtém um novo CSRF token
   */
  getCsrfToken: async (): Promise<string> => {
    try {
      await api.refreshCsrfToken();
      const token = api.getCsrfToken();
      
      if (!token) {
        throw new Error('Falha ao obter CSRF token');
      }
      
      return token;
    } catch (error) {
      console.error('❌ Erro ao obter CSRF token:', error);
      throw error;
    }
  },

  /**
   * Atualiza dados do usuário na sessão
   */
  updateUserData: (user: Partial<User>): void => {
    const currentUser = authService.getCurrentUser();
    if (currentUser) {
      const updatedUser = { ...currentUser, ...user };
      sessionStorage.setItem('user', JSON.stringify(updatedUser));
    }
  },

  /**
   * Obtém URL de autenticação Google
   */
  getGoogleAuthUrl: async (): Promise<string> => {
    try {
      console.log('🔐 Obtendo URL de autenticação Google...');
      
      const response = await api.get(API_ENDPOINTS.auth.googleUrl || '/api/auth/google/url');
      const data = response.data as { success: boolean; authUrl: string };
      
      if (data.success && data.authUrl) {
        console.log('✅ URL Google obtida:', data.authUrl);
        return data.authUrl;
      } else {
        throw new Error('URL de autenticação não retornada');
      }
    } catch (error) {
      console.error('❌ Erro ao obter URL Google:', error);
      throw error;
    }
  },

  /**
   * Faz login via Google OAuth
   */
  loginWithGoogle: async (): Promise<string> => {
    try {
      console.log('🔐 Iniciando login Google...');
      
      // Obter URL de autenticação
      const authUrl = await authService.getGoogleAuthUrl();
      
      // Retornar URL para redirecionamento
      return authUrl;
    } catch (error) {
      console.error('❌ Erro no login Google:', error);
      throw error;
    }
  },

  /**
   * Processa callback de sucesso do Google
   */
  processGoogleCallback: async (userData: any): Promise<void> => {
    try {
      console.log('✅ Processando callback Google:', userData);
      
      // Salvar dados do usuário
      sessionStorage.setItem('user', JSON.stringify(userData));
      sessionStorage.removeItem('selectedProjectId');
      
      console.log('✅ Dados Google salvos com sucesso');
    } catch (error) {
      console.error('❌ Erro ao processar callback Google:', error);
      throw error;
    }
  },

  /**
   * Obtém o token de autenticação (para compatibilidade)
   * Como usamos cookies httpOnly, retorna null mas indica que está autenticado
   */
  getToken: (): string | null => {
    // Este serviço usa cookies httpOnly por segurança
    // Verificamos se o usuário está autenticado via sessionStorage
    return authService.isAuthenticated() ? 'cookie-auth' : null;
  },

  /**
   * Limpa todos os dados de autenticação
   */
  clearAuthData: (): void => {
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('selectedProjectId');
    sessionStorage.removeItem('selectedAgentId');
    sessionStorage.removeItem('fromAgentsList');
    sessionStorage.removeItem('prev_auth_state');
    sessionStorage.removeItem('logging_out');
    api.clearCsrfToken();
    clearAuthCache();
  },

  /**
   * Troca o contexto de projeto do usuário (regenera JWT)
   */
  switchProject: async (projectId: string): Promise<{ success: boolean; projectId: string; projectName?: string; role?: string }> => {
    try {
      console.log('🔄 Trocando projeto:', projectId);

      const response = await api.post(
        API_ENDPOINTS.auth.switchProject,
        { projectId }
      );

      const data = response.data;

      if (data.success) {
        console.log('✅ Projeto alterado com sucesso:', {
          projectId: data.projectId,
          projectName: data.projectName,
          role: data.role
        });

        // Atualizar projectId no user do sessionStorage
        const userStr = sessionStorage.getItem('user');
        if (userStr) {
          const user = JSON.parse(userStr);
          user.projectId = data.projectId;
          sessionStorage.setItem('user', JSON.stringify(user));
        }

        // Atualizar selectedProjectId
        sessionStorage.setItem('selectedProjectId', data.projectId);

        // Atualizar cache
        const cachedUser = authService.getCurrentUser();
        if (cachedUser) {
          cachedUser.projectId = data.projectId;
          setAuthCache(true, cachedUser);
        }

        return data;
      } else {
        throw new Error(data.message || 'Erro ao trocar projeto');
      }
    } catch (error: any) {
      console.error('❌ Erro ao trocar projeto:', error);

      const errorMessage = error.response?.data?.message ||
        error.message ||
        'Erro ao trocar projeto';

      throw new Error(errorMessage);
    }
  },

  /**
   * Valida token de convite
   */
  validateInviteToken: async (token: string): Promise<any> => {
    try {
      console.log('🔍 Validando token de convite...');
      
      const response = await api.get(`/api/users/invite/${token}/validate`);
      return response.data;
    } catch (error: any) {
      console.error('❌ Erro ao validar token de convite:', error);
      throw error;
    }
  },

  /**
   * Aceita convite
   */
  acceptInvite: async (token: string, data: { name?: string; password?: string }): Promise<any> => {
    try {
      console.log('✅ Aceitando convite...');
      
      const response = await api.post(`/api/users/invite/${token}/accept`, data);
      
      if (response.data.success && response.data.data?.authToken) {
        // Salvar token de autenticação se fornecido
        const { userId, email, projectId } = response.data.data;
        
        const userData = {
          id: userId,
          email: email,
          name: data.name || email.split('@')[0],
          projectId: projectId
        };
        
        sessionStorage.setItem('user', JSON.stringify(userData));
        sessionStorage.removeItem('selectedProjectId');
        
        // Atualizar cache de autenticação
        setAuthCache(true, userData);
        
        console.log('✅ Convite aceito e usuário autenticado');
      }
      
      return response.data;
    } catch (error: any) {
      console.error('❌ Erro ao aceitar convite:', error);
      throw error;
    }
  }
};

export default authService;